import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const EASE = 'cubic-bezier(0.23,1,0.32,1)';

function useReveal(ref) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const targets = el.querySelectorAll('[data-reveal]');
    if (!targets.length) return;
    targets.forEach((t, i) => {
      gsap.fromTo(t, { opacity: 0, y: 22 }, {
        opacity: 1, y: 0, duration: 0.65, delay: i * 0.08, ease: EASE,
        scrollTrigger: { trigger: t, start: 'top 88%', toggleActions: 'play none none none' },
      });
    });
    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, [ref]);
}

/* ─── Icons ─── */
function Icon({ path, filled }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill={filled ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke={filled ? 'none' : 'currentColor'} strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d={path} />
    </svg>
  );
}
const ICONS = {
  pin:   'M15 10.5a3 3 0 11-6 0 3 3 0 016 0zM19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  phone: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  mail:  'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
};
const WA_ICON = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z';

const INPUT_CLS = 'w-full px-4 py-3 bg-cream-light border border-wood/15 rounded-sm font-sans text-sm text-wood placeholder:text-taupe/40 focus:outline-none focus:border-terracotta transition-colors duration-200';

/* ─── Hero ─── */
function Hero() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;
    const tl = gsap.timeline();
    tl.fromTo(el.querySelector('h1'), { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.75, ease: EASE })
      .fromTo(el.querySelector('p'),  { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.6,  ease: EASE }, '-=0.4');
    return () => tl.kill();
  }, []);

  return (
    <section
      ref={ref}
      className="flex items-center bg-wood overflow-hidden"
      style={{ minHeight: '40vh' }}
      aria-label="Contáctanos"
    >
      <div className="container-custom py-20">
        <div className="max-w-xl">
          <span className="font-sans text-xs tracking-[0.3em] uppercase text-gold mb-5 block">
            Estamos en Tonalá, Jalisco
          </span>
          <h1 className="font-serif text-display-xs md:text-display-sm text-white mb-4 leading-tight">
            Contáctanos
          </h1>
          <div className="w-14 h-px bg-gold mb-5" />
          <p className="font-sans text-base text-cream/75 leading-relaxed">
            Cotiza sin salir de casa. Respuesta en menos de 24 horas.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Contact Section ─── */
function ContactMain() {
  const ref = useRef(null);
  useReveal(ref);

  const INITIAL = { nombre: '', email: '', telefono: '', ciudad: '', tipo: '', descripcion: '' };
  const [form, setForm] = useState(INITIAL);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Hola Del Sol Muebles, soy ${form.nombre} de ${form.ciudad}.\n\nProyecto: ${form.tipo}\nDescripción: ${form.descripcion}\n\nEmail: ${form.email}\nTeléfono: ${form.telefono}`
    );
    window.open(`https://wa.me/523338283729?text=${msg}`, '_blank');
  };

  const info = [
    {
      icon: ICONS.pin,
      label: 'Dirección',
      content: (
        <address className="font-sans text-sm text-taupe not-italic leading-relaxed">
          And. Tonaltecas 200-L 21<br />
          Tonalá Centro, 45400<br />
          Tonalá, Jalisco, México
        </address>
      ),
    },
    {
      icon: ICONS.phone,
      label: 'Teléfono',
      content: (
        <a href="tel:+523338283729" className="font-sans text-sm text-taupe hover:text-terracotta transition-colors">
          +52 33 3828 3729
        </a>
      ),
    },
    {
      icon: null,
      wa: true,
      label: 'WhatsApp',
      content: (
        <a
          href="https://wa.me/523338283729"
          target="_blank"
          rel="noopener noreferrer"
          className="font-sans text-sm text-taupe hover:text-terracotta transition-colors"
        >
          Enviar mensaje por WhatsApp
        </a>
      ),
    },
    {
      icon: ICONS.mail,
      label: 'Email',
      content: (
        <a href="mailto:info@delsolmuebles.com" className="font-sans text-sm text-taupe hover:text-terracotta transition-colors">
          info@delsolmuebles.com
        </a>
      ),
    },
    {
      icon: ICONS.clock,
      label: 'Horario',
      content: (
        <p className="font-sans text-sm text-taupe leading-relaxed">
          Lunes a Viernes: 10:00 – 18:00<br />
          <span className="text-taupe/60">Sábados y Domingos: Cerrado</span>
        </p>
      ),
    },
  ];

  return (
    <section ref={ref} className="section-padding bg-cream">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">

          {/* ── Left: Contact Info ── */}
          <div data-reveal>
            <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
              Información de Contacto
            </span>
            <h2 className="font-serif text-display-xs text-wood mb-8">
              Encuéntranos en Tonalá
            </h2>

            <div className="space-y-7 mb-10" itemScope itemType="https://schema.org/LocalBusiness">
              <meta itemProp="name" content="Del Sol Muebles" />
              <meta itemProp="telephone" content="+523338283729" />
              <meta itemProp="email" content="info@delsolmuebles.com" />

              {info.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0 text-terracotta">
                    {item.wa ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d={WA_ICON} />
                      </svg>
                    ) : (
                      <Icon path={item.icon} />
                    )}
                  </div>
                  <div>
                    <p className="font-sans text-xs tracking-widest uppercase text-taupe/60 mb-1">{item.label}</p>
                    {item.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Google Maps */}
            <div className="rounded-sm overflow-hidden shadow-md border border-wood/10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3732.3!2d-103.2338!3d20.6237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sTonal%C3%A1%2C%20Jalisco!5e0!3m2!1ses!2smx!4v1"
                width="100%"
                height="300"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                title="Del Sol Muebles — Tonalá, Jalisco"
              />
            </div>
          </div>

          {/* ── Right: Form ── */}
          <div data-reveal>
            <span className="font-sans text-xs tracking-[0.25em] uppercase text-terracotta/80 mb-4 block">
              Cuéntanos tu Proyecto
            </span>
            <h2 className="font-serif text-display-xs text-wood mb-3">
              Cuéntanos tu Proyecto
            </h2>
            <p className="font-sans text-sm text-taupe mb-8 leading-relaxed">
              Este formulario envía a nuestro WhatsApp — respuesta en menos de 24 horas.
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                  Nombre *
                </label>
                <input
                  type="text"
                  name="nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Tu nombre"
                  className={INPUT_CLS}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@email.com"
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={form.telefono}
                    onChange={handleChange}
                    placeholder="+52 33 XXXX XXXX"
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Ciudad
                  </label>
                  <input
                    type="text"
                    name="ciudad"
                    value={form.ciudad}
                    onChange={handleChange}
                    placeholder="Guadalajara, CDMX…"
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                    Tipo de Mueble
                  </label>
                  <select
                    name="tipo"
                    value={form.tipo}
                    onChange={handleChange}
                    className={INPUT_CLS}
                  >
                    <option value="">Seleccionar…</option>
                    <option value="Mesa de Comedor">Mesa de Comedor</option>
                    <option value="Sillas / Bancos">Sillas / Bancos</option>
                    <option value="Mueble para Recámara">Mueble para Recámara</option>
                    <option value="Mueble de Exterior / Terraza">Mueble de Exterior / Terraza</option>
                    <option value="Proyecto Comercial (restaurante, bar, hotel)">Proyecto Comercial</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-sans text-[10px] tracking-[0.2em] uppercase text-taupe mb-2">
                  Descripción del Proyecto *
                </label>
                <textarea
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Cuéntanos qué necesitas: medidas, madera, acabado, cantidad…"
                  className={`${INPUT_CLS} resize-none`}
                />
              </div>

              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-terracotta text-white font-sans text-sm font-medium tracking-widest uppercase rounded-sm hover:bg-terracotta-dark transition-colors duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d={WA_ICON} />
                </svg>
                Enviar por WhatsApp
              </button>

              <p className="font-sans text-[10px] text-taupe/50 text-center tracking-wider">
                Serás redirigido a WhatsApp con tu mensaje pre-redactado
              </p>
            </form>

            {/* Direct WhatsApp */}
            <div className="mt-8 pt-8 border-t border-wood/10">
              <p className="font-sans text-sm text-taupe mb-4 text-center">¿Prefieres escribirnos directamente?</p>
              <a
                href="https://wa.me/523338283729"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-[#25D366] text-white font-sans text-sm font-medium tracking-widest uppercase rounded-sm hover:bg-[#1ebe5d] transition-colors duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d={WA_ICON} />
                </svg>
                Abrir WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── GEO / Local SEO Section ─── */
function GeoSection() {
  const ref = useRef(null);
  useReveal(ref);

  return (
    <section ref={ref} className="section-padding bg-wood">
      <div className="container-custom max-w-3xl mx-auto text-center" data-reveal>
        <span className="font-sans text-xs tracking-[0.3em] uppercase text-gold mb-5 block">
          Tonalá, Jalisco
        </span>
        <h2 className="font-serif text-display-xs md:text-display-sm text-white mb-6">
          Visítanos en Tonalá
        </h2>
        <div className="w-12 h-px bg-gold mx-auto mb-8" />
        <p className="font-sans text-base text-cream/75 leading-relaxed mb-8">
          Tonalá, Jalisco es el principal centro artesanal de México. Nuestro taller está en el
          corazón de este distrito artesanal, rodeado de décadas de tradición en carpintería y
          muebles de madera. Más de 500 talleres y tiendas hacen de Tonalá el destino obligado
          del diseño mexicano.
        </p>
        <p className="font-sans text-base text-cream/75 leading-relaxed mb-10">
          Visítanos de lunes a viernes de 10:00 a 18:00, o cotiza sin salir de casa por WhatsApp.
          Enviamos a todo México con embalaje especializado.
        </p>
        <a
          href="https://wa.me/523338283729?text=Hola%20Del%20Sol%20Muebles%2C%20quisiera%20visitar%20su%20taller"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold"
        >
          Agendar Visita por WhatsApp
        </a>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function Contacto() {
  return (
    <main>
      <Hero />
      <ContactMain />
      <GeoSection />
    </main>
  );
}
